<template>
  <div class="recipient-form">
    <checkbox-input
      :id="`gift-card-recipient-show-${section.id}`"
      v-model="local.enableForm"
      class="recipient-form__checkbox"
      :label="$string('product.form.send_as_gift')"
      :name="controlFlag"
    />

    <div
      v-if="local.enableForm"
      class="recipient-form__container form-group"
    >
      <div class="form-group__field">
        <label
          class="form-group__label"
          :for="`gift-card-recipient-email-${section.id}`"
        >
          <span v-text="$string('product.recipient_form.email.label')" />

          <abbr
            :title="$string('product.recipient_form.required.label')"
            v-text="$string('product.recipient_form.required.symbol')"
          />
        </label>

        <input
          :id="`gift-card-recipient-email-${section.id}`"
          v-model="local.fields['Recipient email']"
          class="form-group__input"
          name="properties[Recipient email]"
          :placeholder="$string('product.recipient_form.email.placeholder')"
          required
          type="email"
        >
      </div>

      <div class="form-group__field">
        <label
          class="form-group__label"
          :for="`gift-card-recipient-name-${section.id}`"
        >
          <span v-text="$string('product.recipient_form.name.label')" />

          <span
            class="form-group__optional"
            v-text="$string('product.recipient_form.optional')"
          />
        </label>

        <input
          :id="`gift-card-recipient-name-${section.id}`"
          v-model="local.fields['Recipient name']"
          class="form-group__input"
          name="properties[Recipient name]"
          :placeholder="$string('product.recipient_form.name.placeholder')"
          type="text"
        >
      </div>

      <div class="form-group__field">
        <label
          class="form-group__label"
          :for="`gift-card-recipient-message-${section.id}`"
        >
          <span v-text="$string('product.recipient_form.message.label')" />

          <span
            class="form-group__optional"
            v-text="$string('product.recipient_form.optional')"
          />
        </label>

        <textarea
          :id="`gift-card-recipient-message-${section.id}`"
          v-model="local.fields.Message"
          class="form-group__input"
          :maxlength="messageLimit"
          name="properties[Message]"
          :placeholder="$string('product.recipient_form.message.placeholder')"
          type="text"
        />

        <span
          class="form-group__footer"
          v-text="$string(
            'product.recipient_form.message.limit',
            {
              pluralise: messageRemaining,
              replace: { remaining: messageRemaining },
            },
          )"
        />
      </div>

      <div class="form-group__field">
        <label
          class="form-group__label"
          :for="`gift-card-recipient-send-on-${section.id}`"
        >
          <span v-text="$string('product.recipient_form.send_on.label')" />

          <span
            class="form-group__optional"
            v-text="$string('product.recipient_form.optional')"
          />
        </label>

        <input
          :id="`gift-card-recipient-send-on-${section.id}`"
          v-model="local.fields['Send on']"
          class="form-group__input"
          autocomplete="send_on"
          name="properties[Send on]"
          pattern="\d{4}-\d{2}-\d{2}"
          :placeholder="$string('product.recipient_form.send_on.placeholder')"
          type="date"
        >
      </div>
    </div>

    <input
      :id="`gift-card-recipient-control-${section.id}`"
      disabled
      :name="controlFlag"
      type="hidden"
      :value="local.enableForm ? 'on' : null"
    >

    <input
      :id="`gift-card-recipient-timezone-offset-${section.id}`"
      disabled
      name="properties[__shopify_offset]"
      type="hidden"
      :value="local.timezoneOffset"
    >
  </div>
</template>

<script>

/**
 * Vue: Recipient form (product-form)
 * -----------------------------------------------------------------------------
 * Recipient form for gift card products.
 *
 * @param {Object} giftCard - Gift card props.
 * @param {Object} section - Main product section settings.
 *
 */
import CheckboxInput from '~async/utils/checkbox-input/checkbox-input'

export default {
  name: 'RecipientForm',

  components: {
    CheckboxInput,
  },

  props: {
    giftCard: {
      type: Object,
      default: () => ({
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      }),
    },
    section: {
      type: Object,
      default: () => ({
        displayRecipientForm: true,
        id: '',
      }),
      required: true,
    },
  },

  emits: {
    'update-recipient-form': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      controlFlag: 'properties[__shopify_send_gift_card_to_recipient]',
      local: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      messageLimit: 200,
    }
  },

  computed: {

    /**
     * Compute characters remaining in message field.
     * @returns {Number}
     */
    messageRemaining() {
      return this.messageLimit - this.local.fields.Message.length
    },
  },

  watch: {

    /**
     * Watch gift card fields to update locale field values.
     */
    giftCard: {
      handler() {
        this.updateLocalValues()
      },
      deep: true,
    },

    /**
     * Watch local fields to emit update.
     */
    local: {
      handler() {
        this.$emit('update-recipient-form', this.local)
      },
      deep: true,
    },
  },

  mounted() {
    this.updateLocalValues()
  },

  methods: {

    /**
     * Update local values if they differ.
     */
    updateLocalValues() {
      Object.keys(this.giftCard).forEach((key) => {
        if (this.giftCard[key] === this.local[key]) {
          return
        }

        this.local[key] = this.giftCard[key]
      })
    },
  },
}
</script>

<style lang="scss">
@import './recipient-form';
</style>
